import React, { useEffect } from "react";
import { ToggleGroup, ToggleGroupItem } from "ui/ToggleGroup";
import DetailPanel from "ui/DetailPanel";
import { PrivacyLevel } from "util/userUtils";
import { cn } from "util/cn";
import AddButton from "ui/AddButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from 'ui/InputSelect';
import ActorPrivacy from "./ActorPrivacy";
import { ViewerRelation, EntityType } from "util/constants";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import { Comment as CommentData } from 'util/constants';
import RenderComments from "components/Comments/RenderComments";
import api from 'config/axiosConfig';
import { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import { Loader2, Pencil, MoveLeft, MoveRight } from "lucide-react";
import { useUser } from "util/UserContext";
import ImpactCardRefactored from "components/Impacts/ImpactCardRefactored";
import { getEntityDetails } from "util/getObjectDetails";
import AlignmentModal from "components/Alignment/AlignmentModal";
import SendNotificationModal from "components/Common/SendNotificationModal";
import { apiRoutes } from "util/apiRoutes";

interface AlignmentTileProps {
  statement: any;
  statementNumber: number;
  response: number;
  importance: number;
  /**
   * Whether the target actor (profile user or team) has an actual response for this statement.
   * If false, the UI should render as no selection (no shaded/selected option) rather than Neutral.
   */
  targetHasResponse?: boolean;
  note?: string;
  commentId?: number;
  privacy?: PrivacyLevel;
  currentUserResponse?: number;
  currentUserImportance?: number;
  currentUserViewerRelation: ViewerRelation;
  actorType: "user" | "team";
  onRespond?: (statementId: number) => void;
  hasResponded?: boolean;
  statements?: Array<{
    id: number;
    statement: any;
    hasResponse: boolean;
  }>;
  onStatementChange?: (statementId: string | number) => void;
  isPanelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
  showSummaryOption?: boolean;
  targetActorId?: number;
  targetActorName?: string;
}

const AlignmentTile: React.FC<AlignmentTileProps> = ({
  statement,
  statementNumber,
  response,
  importance,
  targetHasResponse,
  note,
  commentId,
  privacy,
  currentUserResponse,
  currentUserImportance,
  currentUserViewerRelation,
  actorType = "user",
  onRespond,
  hasResponded = true,
  statements = [],
  onStatementChange,
  isPanelOpen = false,
  onPanelOpenChange,
  showSummaryOption = false,
  targetActorId,
  targetActorName
}) => {
  const [comments, setComments] = React.useState<CommentData[]>([]);
  const [isLoadingComments, setIsLoadingComments] = React.useState(false);
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const [impact, setImpact] = React.useState<any | null>(null);
  const [showAlignmentModal, setShowAlignmentModal] = React.useState(false);
  const [showAskToRespondModal, setShowAskToRespondModal] = React.useState(false);

  // Dimension categories mapping
  const ALIGNMENT_DIMENSIONS = [
    { key: "nationalism_cosmopolitanism", dimension: "Diplomacy", label1: "Nationalism", label2: "Cosmopolitanism" },
    { key: "theocracy_secularism", dimension: "Religion", label1: "Theocracy", label2: "Secularism" },
    { key: "authority_liberty", dimension: "State", label1: "Authority", label2: "Liberty" },
    { key: "capitalism_socialism", dimension: "Economics", label1: "Capitalism", label2: "Socialism" },
    { key: "dogma_science", dimension: "Beliefs", label1: "Dogma", label2: "Science" },
    { key: "autocracy_democracy", dimension: "Government", label1: "Autocracy", label2: "Democracy" },
    { key: "conservatism_progressivism", dimension: "Traditions", label1: "Conservatism", label2: "Progressivism" },
    { key: "content_discontent", dimension: "Sentiment", label1: "Status Quo", label2: "Reformation" }
  ];

  // Get dimension labels for this teabate
  const getDimensionLabels = () => {
    const dimension = impact?.properties?.dimension?.toLowerCase();
    if (!dimension) return null;
    
    // Find category by matching either label1 or label2 with the dimension
    const category = ALIGNMENT_DIMENSIONS.find(cat => 
      cat.label1.toLowerCase() === dimension || cat.label2.toLowerCase() === dimension
    );
    if (!category) return null;
    
    // Determine which side the teabate dimension represents
    const isLabel1 = category.label1.toLowerCase() === dimension;
    
    // The "Agree" side (right) corresponds to the teabate dimension, "Disagree" side (left) is the opposite
    return {
      disagreeLabel: isLabel1 ? category.label2 : category.label1, // Left side (opposite)
      agreeLabel: isLabel1 ? category.label1 : category.label2,     // Right side (matches teabate dimension)
    };
  };

  const dimensionLabels = getDimensionLabels();

  useEffect(() => {
    const fetchImpactDetails = async () => {
      if (statementNumber) {
        const impactDetails = await getEntityDetails('impact', statementNumber);
        setImpact(impactDetails);
      }
    };
    fetchImpactDetails();
  }, [statementNumber]);

  useEffect(() => {
    if (commentId) {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentId]);

  const fetchComments = async () => {
    setIsLoadingComments(true);
    try {
      const { data } = await api.get(apiRoutes.comments.replies(commentId as number));
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleAddReply = async (parentId: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => {
    try {
      await api.post(apiRoutes.comments.replies(parentId), { content, privacy, coauthors });
      fetchComments();
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const handleUpdateComment = async (commentId: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => {
    try {
      await api.put(apiRoutes.comments.byId(commentId), { content, privacy, addedCoauthors, removedCoauthors });
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await api.delete(apiRoutes.comments.byId(commentId));
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };



  const responseOptions = [
    { value: -2, label: "Strongly Disagree" },
    { value: -1, label: "Disagree" },
    { value: 0, label: "Neutral" },
    { value: 1, label: "Agree" },
    { value: 2, label: "Strongly Agree" },
  ];

  const importanceOptions = [
    { value: -2, label: "Very Unimportant" },
    { value: -1, label: "Unimportant" },
    { value: 0, label: "Neutral" },
    { value: 1, label: "Important" },
    { value: 2, label: "Very Important" },
  ];


  const handleStatementChange = (statementId: string) => {
    if (onStatementChange) {
        if (statementId === 'summary') {
            onStatementChange('summary');
        } else {
            const id = parseInt(statementId);
            onStatementChange(id);
        }
    }
  };

  const showStatementSelector = statements.length > 0 && onStatementChange;

  return (
    <div className="bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal p-1 rounded-lg space-y-2">
        {showStatementSelector ? (
          <div className="mb-2">
            <Select 
              value={statementNumber.toString()}
              onValueChange={handleStatementChange}
            >
              <SelectTrigger className="input-select text-xs px-2 py-1.5 text-left font-medium text-eoe-text dark:text-eoe-dark-text">
                <SelectValue placeholder="Select statement">
                      <EntityCompactCard 
                        entityId={statementNumber}
                        entityType={'impact' as EntityType}
                        showActions={false}
                        showMeta={false}
                        showLink={false}
                      />
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="md:max-w-[600px] border border-eoe-border-main dark:border-eoe-dark-border-main">
                <SelectGroup>
                  <SelectLabel>Answered</SelectLabel>
                  {statements.filter(s => s.hasResponse).map(stmt => (
                    <SelectItem 
                      key={`answered-${stmt.id}`} 
                      value={stmt.id.toString()} 
                      className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                    >
                      <EntityCompactCard 
                        entityId={stmt.id}
                        entityType={'impact' as EntityType}
                        showActions={false}
                        showMeta={false}
                        showLink={false}
                      />
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Unanswered</SelectLabel>
                  {statements.filter(s => !s.hasResponse).map(stmt => (
                    <SelectItem 
                      key={`unanswered-${stmt.id}`} 
                      value={stmt.id.toString()} 
                      className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                    >
                      <EntityCompactCard 
                        entityId={stmt.id}
                        entityType={'impact' as EntityType}
                        showActions={false}
                        showMeta={false}
                        showLink={false}
                      />
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text bg-eoe-icon dark:bg-eoe-dark-icon rounded-lg p-1">
                <span className="border rounded-full px-1 border-eoe-text dark:border-eoe-dark-text">{statementNumber}</span> {statement}
            </h3>
          </div>
        )}

        <div className="flex justify-between gap-1">
          <div>
          {actorType === 'user' && (
          <ActorPrivacy 
              privacy={privacy || 'public' as PrivacyLevel}
              settingKey={`about_alignment_${statementNumber}`}
              onPrivacyChange={() => {}}
              actorType={actorType}
              editMode={false}
              showDropdown={false}
              miniIcon={false}
              disabled={true}
              viewerRelation={currentUserViewerRelation}
              className="opacity-50"
          />
          )}
          </div>
          {(currentUserResponse !== undefined || currentUserImportance !== undefined) && (
              <div className="flex justify-center items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-eoe-blue"></div>
                  <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                  Your response
                  </span>
                  <button
                    onClick={() => setShowAlignmentModal(true)}
                    className="p-1 rounded hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover transition-colors"
                    title="Edit your alignment"
                  >
                    <Pencil className="size-3 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke" />
                  </button>
              </div>
          )}
        </div>

      {!awaitingUser && currentUser && (
      <div className="mb-4">
        <div className="relative flex justify-center items-center mb-1">
          {dimensionLabels ? (
            <>
              {/* Left side dimension label */}
              <div className="absolute left-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <MoveLeft className="size-3" />
                <span>{dimensionLabels.disagreeLabel}</span>
              </div>
              
              {/* Centered "Alignment" text */}
              <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">Alignment</span>
              
              {/* Right side dimension label */}
              <div className="absolute right-1 flex items-center gap-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <span>{dimensionLabels.agreeLabel}</span>
                <MoveRight className="size-3" />
              </div>
            </>
          ) : (
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub font-semibold">Alignment</p>
          )}
        </div>
        <ToggleGroup type="single" value={(targetHasResponse ?? true) ? response.toString() : undefined} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {responseOptions.map((option) => {
            const targetSelected = (targetHasResponse ?? true) && response === option.value;
            const currentSelected = currentUserResponse === option.value;
            const currentClass = currentSelected
              ? (targetSelected
                  ? "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue"
                  : " bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text")
              : "";
            return (
              <ToggleGroupItem
                key={option.value}
                value={option.value.toString()}
                disabled={true}
                className={cn(
                  "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                  targetSelected && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                  currentClass
                )}
                aria-label={option.label}
                data-state="off"
              >
              {option.value === -2 && "Strongly Disagree"}
              {option.value === -1 && "Disagree"}
              {option.value === 0 && "Neutral"}
              {option.value === 1 && "Agree"}
              {option.value === 2 && "Strongly Agree"}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>
      )}

      {!awaitingUser && currentUser && (
      <div className="mb-4">
        <div className="flex justify-center items-center mb-1">
          <p className="text-xs font-semibold text-center text-eoe-text-sub dark:text-eoe-dark-text-sub">Importance</p>
        </div>
        <ToggleGroup type="single" value={(targetHasResponse ?? true) ? importance.toString() : undefined} className="justify-between w-full [&>*:first-child]:rounded-l-2xl [&>*:last-child]:rounded-r-2xl">
          {importanceOptions.map((option) => {
            const targetSelected = (targetHasResponse ?? true) && importance === option.value;
            const currentSelected = currentUserImportance === option.value;
            const currentClass = currentSelected
              ? (targetSelected
                  ? "bg-eoe-entity-user dark:bg-eoe-entity-user border-[6px] sm:border-8 border-eoe-blue dark:border-eoe-blue"
                  : " bg-eoe-blue dark:bg-eoe-blue text-white dark:text-eoe-dark-text")
              : "";
            return (
              <ToggleGroupItem
                key={option.value}
                value={option.value.toString()}
                disabled={true}
                className={cn(
                  "flex-1 text-[9px] sm:text-xxs md:text-xs font-light leading-snug data-[state=on]:text-[9px] bg-eoe-icon dark:bg-eoe-dark-icon rounded-none",
                  targetSelected && "bg-eoe-entity-user dark:bg-eoe-entity-user text-white dark:text-eoe-dark-text",
                  currentClass
                )}
                aria-label={option.label}
                data-state="off"
              >
              {option.value === -2 && "Very Unimportant"}
              {option.value === -1 && "Unimportant"}
              {option.value === 0 && "Neutral"}
              {option.value === 1 && "Important"}
              {option.value === 2 && "Very Important"}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>
      )}

      {/* Invite target to respond when they haven't, show below "Your response" section */}
      {!awaitingUser && currentUser && (currentUserResponse !== undefined || currentUserImportance !== undefined) && (targetHasResponse === false) && (
        <div className="-mt-2 mb-2">
          <button
            type="button"
            className="px-2 py-1 rounded-full text-xxs font-medium border border-eoe-border-main dark:border-eoe-dark-border-main text-eoe-text-sub dark:text-eoe-dark-text-sub hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
            onClick={() => {
              if (!currentUser) {
                setShowAuthModal(true);
                return;
              }
              if (targetActorId && targetActorName) {
                setShowAskToRespondModal(true);
              }
            }}
          >
            Ask them to respond
          </button>
        </div>
      )}

      {note && hasResponded && (
        <DetailPanel 
          title="Note" 
          className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
          open={isPanelOpen}
          onOpenChange={onPanelOpenChange}
        >
          <p className="text-left text-xs text-eoe-text dark:text-eoe-dark-text whitespace-pre-wrap">{note}</p>
        </DetailPanel>
      )}

      {commentId && (
        <DetailPanel
          title="Discuss"
          className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
        >
          <div className="flex py-1 text-left w-full">
          {isLoadingComments ? (
            <div className="flex justify-center items-center">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (
          <RenderComments
            comments={comments}
            maxDepth={3}
            isAllCollapsed={false}
            onAddReply={handleAddReply}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            commentable_type="impact"
            commentable_id={statementNumber}
            isEntitySection={true}
            showEntityCompactCard={false}
          />
          )}
          </div>
        </DetailPanel>
      )}

      {!awaitingUser && currentUser && (
        <>
        <DetailPanel
          title={`${!hasResponded ? "Respond to Teabate" : "View Teabate"}`}
          className="text-left text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-bg-hover dark:bg-eoe-dark-bg-modal-hover"
        >
          {impact && (
            <ImpactCardRefactored
              impact={impact}
              showMeta={true}
              showActions={true}
              isEntitySection={false}
            />
          )}
        </DetailPanel>
        </>
      )}

      {!awaitingUser && !currentUser && (
        <>
          <AddButton
            tooltipTitle="Respond to this statement in order to view their response"
            buttonText="Respond to Teabate"
            className="z-10 rounded-3xl w-full min-h-[100px] h-[40%]"
            noPlus={true}
            onClick={() => {
              if (onRespond) {
                onRespond(statementNumber);
              }
            }}
          />
          <p className="text-center text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">You can keep your response private, if you want</p>
        </>
      )}
      
      {/* Alignment Modal for editing */}
      {showAlignmentModal && impact && impact.kind === 'teabate' && (
        <AlignmentModal
          isOpen={showAlignmentModal}
          onClose={() => {
            setShowAlignmentModal(false);
            // Refresh the page to show updated alignment
            window.location.reload();
          }}
          teabate={impact}
          stance={currentUserResponse && currentUserResponse > 0 ? "agree" : "disagree"}
          editMode={true}
        />
      )}

      {/* Ask to Respond Modal */}
      {showAskToRespondModal && targetActorId && targetActorName && impact && (
        <SendNotificationModal
          isOpen={showAskToRespondModal}
          onClose={() => setShowAskToRespondModal(false)}
          recipientId={targetActorId}
          recipientUsername={targetActorName}
          defaultMessage={`I'd love to hear your perspective on this teabate: "${statement}"`}
          notificationType="share_perspective"
          entityType="teabate"
          entityId={statementNumber}
          relEntityType={impact.properties?.teabate_issue_type || "issue"}
          relEntityId={impact.properties?.teabate_issue_id}
          properties={{
            statement: statement
          }}
          buttonText="Send request"
          title="Ask them to share their perspective"
          useTextArea={true}
        />
      )}
    </div>
  );
};

export default AlignmentTile;
